import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, IndianRupee, Receipt, Download, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate, errMsg } from "../../lib/api";
import { PageHeader, StatusBadge, StatCard, TableShell, TR, TD, LoadingRows, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));

function downloadInvoice(inv, partnerName) {
  const w = window.open("", "_blank");
  w.document.write(`<html><head><title>${esc(inv.invoice_no)}</title><style>
    body{font-family:system-ui;padding:48px;color:#111}h1{font-size:28px}table{width:100%;border-collapse:collapse;margin-top:24px}
    td,th{border-bottom:1px solid #eee;padding:10px;text-align:left;font-size:14px}.total{font-size:20px;font-weight:800}
  </style></head><body>
  <h1>MILTROO — Tax Invoice</h1><p>${esc(inv.invoice_no)} · ${esc(inv.period)} · Billed to: ${esc(partnerName)}</p>
  <table><tr><th>Description</th><th>Amount</th></tr>
  ${(inv.line_items || []).map((l) => `<tr><td>${esc(l.label)}</td><td>₹${esc(l.amount)}</td></tr>`).join("")}
  <tr><td class="total">Total</td><td class="total">₹${esc(inv.amount)}</td></tr></table>
  <p>Status: ${esc((inv.status || "").toUpperCase())}</p><script>window.print()</script></body></html>`);
  w.document.close();
}

export default function PartnerBilling() {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => { api.get("/partner/billing").then((r) => setB(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  if (!b) return <LoadingRows rows={4} />;

  const pay = async (invoice) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { ref_id: invoice.id });
      navigate(`${data.checkout_url}?back=/partner/billing`);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  const inv = b.current_invoice;

  return (
    <div data-testid="partner-billing">
      <PageHeader title="Billing & Subscription" subtitle="3-tier packages — Starter, Growth or Unlimited — auto-calculated from active websites." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard icon={Globe} label="Active Websites" value={b.active_websites} sub={b.plan ? `${b.plan.name} package · ${b.plan.range}` : "3-tier model"} testid="billing-sites" />
        <StatCard icon={IndianRupee} label="Monthly Amount" value={fmtINR(b.monthly_amount)} sub={b.plan?.per_site ? `${b.active_websites} sites × ₹${b.plan.per_site}` : "Flat rate — auto-recalculated as sites change"} testid="billing-amount" delay={0.05} />
        <div data-testid="billing-status-card" className="bg-card border border-border rounded-md p-6">
          <span className="overline-label">Subscription</span>
          <div className="mt-3"><StatusBadge status={b.subscription_status} /></div>
          {b.trial_ends_at && b.subscription_status === "trial" && (
            <p className="text-xs text-muted-foreground mt-2">Trial ends {fmtDate(b.trial_ends_at)}</p>
          )}
          <p className="text-xs text-muted-foreground mt-2">Grace period: {b.grace_days} days</p>
        </div>
      </div>

      {/* Current invoice */}
      {inv && (
        <div data-testid="current-invoice" className="bg-primary text-primary-foreground rounded-md p-6 sm:p-8 mb-8 flex flex-wrap items-center gap-6 sm:gap-8">
          <div className="flex-1 min-w-0 w-full sm:w-auto">
            <span className="overline-label opacity-60">Current invoice · {inv.invoice_no}</span>
            <p className="font-display font-extrabold text-4xl tracking-tighter mt-3">{fmtINR(inv.amount)}</p>
            <div className="mt-4 space-y-1 text-sm opacity-80">
              {(inv.line_items || []).map((l, i) => <p key={i}>{l.label} = {fmtINR(l.amount)}</p>)}
            </div>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-3 w-full sm:w-auto">
            <StatusBadge status={inv.status} />
            {inv.status !== "paid" && (
              <Button data-testid="pay-now-button" onClick={() => pay(inv)} disabled={busy}
                className="rounded-full font-bold bg-white text-black hover:bg-white/90 h-12 px-8 active:scale-95 transition-transform">
                <CreditCard className="h-4 w-4 mr-2" /> {busy ? "Opening…" : "Pay with PhonePe"}
              </Button>
            )}
            <Button data-testid="download-invoice" variant="ghost" onClick={() => downloadInvoice(inv, "Partner")}
              className="rounded-full font-bold text-primary-foreground/70 hover:text-primary-foreground">
              <Download className="h-4 w-4 mr-2" /> Download
            </Button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <p className="overline-label mb-4 flex items-center gap-2"><Receipt className="h-4 w-4" /> Invoices</p>
          {b.invoices.length === 0 ? <EmptyState title="No invoices" body="Invoices generate automatically each billing period." /> : (
            <TableShell testid="invoices-table" head={["Invoice", "Period", "Sites", "Amount", "Status", ""]}>
              {b.invoices.map((i) => (
                <TR key={i.id} testid={`invoice-row-${i.id}`}>
                  <TD className="font-mono text-xs font-bold">{i.invoice_no}</TD>
                  <TD className="text-sm">{i.period}</TD>
                  <TD className="font-bold">{i.website_count}</TD>
                  <TD className="font-bold">{fmtINR(i.amount)}</TD>
                  <TD><StatusBadge status={i.status} /></TD>
                  <TD><button data-testid={`invoice-download-${i.id}`} onClick={() => downloadInvoice(i, "Partner")} className="h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"><Download className="h-4 w-4" /></button></TD>
                </TR>
              ))}
            </TableShell>
          )}
        </div>
        <div>
          <p className="overline-label mb-4 flex items-center gap-2"><CreditCard className="h-4 w-4" /> Payment History</p>
          {b.payments.length === 0 ? <EmptyState title="No payments" body="Your PhonePe transactions will appear here." /> : (
            <TableShell testid="payments-table" head={["Transaction", "Amount", "Date", "Status"]}>
              {b.payments.map((p) => (
                <TR key={p.id} testid={`payment-row-${p.id}`}>
                  <TD className="font-mono text-xs font-bold">{p.txn_id}</TD>
                  <TD className="font-bold">{fmtINR(p.amount)}</TD>
                  <TD className="text-xs text-muted-foreground">{fmtDate(p.created_at)}</TD>
                  <TD><StatusBadge status={p.status} /></TD>
                </TR>
              ))}
            </TableShell>
          )}
        </div>
      </div>
    </div>
  );
}
